'use client';

import { useState, useEffect } from 'react';
import { Button, Label, TextInput, Checkbox } from 'flowbite-react';
import { EyeOff, ChevronLeft } from 'lucide-react';
import bgLatto from '../../assets/images/bg-latto.jpg';
import Logo from '../../assets/images/logo.webp';
import EyeOpen from '../../assets/icons/Eye.svg?react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { useAuth } from '../../context/AuthContext';

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  });
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const location = useLocation();
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // Check for success message from location state (e.g., after registration)
  useEffect(() => {
    // First check for session expired flag in sessionStorage
    const sessionExpired = sessionStorage.getItem('sessionExpired');
    if (sessionExpired) {
      setError('Your session has expired. Please log in again.');
      sessionStorage.removeItem('sessionExpired');
    }
    // Then check for success message from location state
    else if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear location state after displaying the message
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    // Redirect authenticated users to account page
    if (isAuthenticated) {
      navigate('/account');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      const response = await authApi.login(formData);

      // save user info to local storage
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('tokens', JSON.stringify(response.tokens));

      if (response?.user && response?.tokens) {
        login(response.user, response.tokens);

        // For iOS devices, reset the zoom level
        if (isIOS) {
          // Temporarily set viewport to force reset zoom
          let metaViewport = document.querySelector('meta[name=viewport]');
          if (metaViewport) {
            metaViewport.content =
              'width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0';
            setTimeout(() => {
              metaViewport.content = 'width=device-width, initial-scale=1.0';

              // Check if there was a previous attempted URL to redirect to
              const redirectPath = sessionStorage.getItem('redirectAfterLogin');
              if (redirectPath) {
                console.log(
                  'Redirecting to previously attempted page:',
                  redirectPath
                );
                sessionStorage.removeItem('redirectAfterLogin'); // Clear it
                // Force reload to clear any cached state
                window.location.href =
                  redirectPath + '?fresh=' + new Date().getTime();
              } else {
                // Force reload to clear any cached state
                window.location.href =
                  '/home' + '?fresh=' + new Date().getTime();
              }
            }, 100);
          }
        } else {
          // Check if there was a previous attempted URL to redirect to
          const redirectPath = sessionStorage.getItem('redirectAfterLogin');
          if (redirectPath) {
            console.log(
              'Redirecting to previously attempted page:',
              redirectPath
            );
            sessionStorage.removeItem('redirectAfterLogin'); // Clear it
            // Force reload to clear any cached state
            window.location.href =
              redirectPath + '?fresh=' + new Date().getTime();
          } else {
            // Force reload to clear any cached state
            window.location.href = '/home' + '?fresh=' + new Date().getTime();
          }
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  return (
    <div className='min-h-screen flex flex-col bg-[#1e3a8a] relative overflow-hidden'>
      {/* Background Balls */}
      <div className='absolute inset-0 z-0'>
        <img
          src={bgLatto}
          alt='Background'
          className='w-full h-full object-cover'
        />
      </div>

      {/* Logo Section */}
      <div className='relative z-10 flex-grow flex flex-col items-center justify-center p-4'>
        <img
          src={Logo}
          className='mb-2 object-cover w-[220px] h-[220px] max-xs:w-[160px] max-xs:h-[160px]  '
        />
      </div>

      {/* Content Section */}
      <div className='relative z-10 bg-white rounded-t-[40px] p-6 w-full '>
        <div className='flex items-center my-6 mb-10  max-xs:my-3 max-xs:mb-5 '>
          {/* Back Button */}
          <button className='text-gray-600' onClick={() => navigate(-1)}>
            <ChevronLeft className='h-6 w-6' />
          </button>

          {/* Form Title */}
          <h1 className='text-gray-800 text-2xl font-bold font-poppins flex-grow text-center '>
            Login
          </h1>
        </div>

        {/* Login Form */}
        <form
          className='space-y-5 max-xs:space-y-3  px-6 my-5'
          onSubmit={handleLogin}
        >
          {error && (
            <div className='text-red-600 text-sm bg-red-50 p-3 rounded-md'>
              {error}
            </div>
          )}

          {successMessage && (
            <div className='text-green-600 text-sm bg-green-50 p-3 rounded-md'>
              {successMessage}
            </div>
          )}

          <div>
            <Label
              htmlFor='identifier'
              className='block text-sm max-xs:text-xs text-[#695C5C;] font-poppins ml-1 mb-2'
            >
              Phone Number or Email<span className='text-red-500'>*</span>
            </Label>
            <TextInput
              id='identifier'
              type='text'
              required
              value={formData.identifier}
              onChange={handleInputChange}
              placeholder='Enter Your Phone Number or Email'
              className='w-full'
              style={{ fontSize: '16px' }}
            />
          </div>

          <div className='relative'>
            <Label
              htmlFor='password'
              className='block text-sm max-xs:text-xs text-[#695C5C;] font-poppins ml-1 mb-2'
            >
              Password<span className='text-red-500'>*</span>
            </Label>
            <TextInput
              id='password'
              type={showPassword ? 'text' : 'password'}
              required
              value={formData.password}
              onChange={handleInputChange}
              placeholder='Enter Your Password'
              className='w-full max-xs:py-1'
              style={{ fontSize: '16px' }}
            />
            <button
              type='button'
              className='absolute right-3 top-1/2 '
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className='h-5 w-5 text-gray-500' />
              ) : (
                <EyeOpen
                  className='w-full h-full object-cover'
                  alt='Show password'
                />
              )}
            </button>
          </div>

          <div className='flex items-center justify-between '>
            <div className='flex items-center'>
              <Checkbox
                id='remember'
                className='mr-2 bg-white checked:bg-black '
              />

              <Label
                htmlFor='remember'
                className='text-sm text-[#12131A;]  max-xs:text-xs  '
              >
                Remember Me
              </Label>
            </div>
            <Link
              to='/forgot-password'
              className='text-[#FF0000;] font-poppins text-sm  max-xs:text-xs  hover:underline'
            >
              Forgot Pin?
            </Link>
          </div>

          <div className='text-[9.5px] text-gray-600  '>
            By login or sign up, you are confirming that you are above 18 years
            old, and agreed to our{' '}
            <Link href='/terms' className='text-blue-600 hover:underline'>
              Terms and Conditions
            </Link>
          </div>

          <Button
            type='submit'
            className='w-full bg-blue-800 hover:bg-blue-900 text-white text-base font-medium py-2 rounded-md max-xs:py-1'
          >
            Login
          </Button>

          <div className='text-center text-base text-black font-manrope font-bold  max-xs:text-sm '>
            Don't have an account?{' '}
            <Link to='/register' className='text-blue-600 hover:underline'>
              Register
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
